import { pluralize } from 'src/lib/format';
import { UniqMap } from 'src/lib/UniqMap';

import { SEAT_PRICE_PER_MONTH } from './billing';
import {
  BILLING_CYCLES,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_ACTIVE,
  SUBSCRIPTION_PLAN_LEGACY,
} from './const';
import { getPlanPerformancePredictionsLimit } from './subscriptionPlan';
import { getSeatsByTier, getWorkspacesByTier } from './subscriptionTier';

const PLAN_FEATURES_MAP = new UniqMap();

const seatsTextFormatter = ({ plan, tier }) => `Includes ${getSeatsByTier(plan, tier)} seats`;

const workspacesTextFormatter = ({ plan, tier }) => {
  const count = getWorkspacesByTier(plan, tier);
  return `${count} ${pluralize(count, 'workspace', 'workspaces')}`;
};

const createSeatsTooltipFormatter =
  (includedSeats, maxSeats) =>
  ({ billingCycle }) => {
    const seatPrice =
      billingCycle === BILLING_CYCLES.annually
        ? SEAT_PRICE_PER_MONTH.annual
        : SEAT_PRICE_PER_MONTH.monthly;
    return `${includedSeats} seats are included, and you can purchase up to ${maxSeats} additional seats for $${seatPrice}/seat.`;
  };

/**
 * Active plans features
 */

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_ACTIVE.basic, {
  features: [
    {
      text: `${getPlanPerformancePredictionsLimit(
        SUBSCRIPTION_PLAN.basic
      )} performance predictions`,
      tooltip: '',
    },
    {
      text: '1 brand voice',
      tooltip:
        'Create 1 brand voice and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip:
        'Choose from our ready-made marketing prompts for emails, social media, ads, landing pages, etc.',
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog in minutes.',
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
    },
    {
      text: workspacesTextFormatter,
    },
  ],
});

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_ACTIVE.starter, {
  featuresTitle: 'Starter plan includes:',
  features: [
    {
      text: `${getPlanPerformancePredictionsLimit(
        SUBSCRIPTION_PLAN.starter
      )} performance predictions`,
      tooltip: '',
    },
    {
      text: '50 performance data rows',
      tooltip: '',
    },
    {
      text: 'Includes 1 seat',
      tooltip: '',
      exclusiveForPricingPage: true,
    },
    {
      text: 'Unlimited copy generation',
      tooltip:
        'You have unlimited words to generate as much content as you need with Anyword’s Data-driven editor and Long form wizard.',
    },
    {
      text: '1 brand voice',
      tooltip:
        'Create 1 brand voice and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip:
        'Choose from our ready-made marketing prompts for emails, social media, ads, landing pages, etc.',
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog in minutes.',
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
    },
    {
      text: workspacesTextFormatter,
      hiddenOnPricingPage: true,
    },
  ],
});

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_ACTIVE.dataDriven, {
  featuresTitle: 'Everything in Starter, plus:',
  features: [
    {
      text: `${getPlanPerformancePredictionsLimit(
        SUBSCRIPTION_PLAN.dataDriven
      )} performance predictions`,
      tooltip: '',
    },
    {
      text: '50 performance data rows',
      tooltip: '',
    },
    {
      text: seatsTextFormatter,
      tooltip: createSeatsTooltipFormatter(3, 10),
      exclusiveForPricingPage: true,
    },
    {
      text: 'Real-time performance predictions for manual edits',
      tooltip: '',
    },
    {
      text: 'Unlimited copy generation',
      tooltip:
        'You have unlimited words to generate as much content as you need with Anyword’s Data-driven editor and Long form wizard.',
      hiddenOnPricingPage: true,
    },
    {
      text: '1 brand voice',
      tooltip:
        'Create 1 brand voice and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
      hiddenOnPricingPage: true,
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
      hiddenOnPricingPage: true,
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip:
        'Choose from our ready-made marketing prompts for emails, social media, ads, landing pages, etc.',
      hiddenOnPricingPage: true,
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog\nin minutes.',
      hiddenOnPricingPage: true,
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
      hiddenOnPricingPage: true,
    },
    {
      text: workspacesTextFormatter,
      tooltip: '',
      hiddenOnPricingPage: true,
    },
  ],
});

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_ACTIVE.business, {
  featuresTitle: 'Everything in Data-Driven, plus:',
  features: [
    {
      text: `${getPlanPerformancePredictionsLimit(
        SUBSCRIPTION_PLAN.business
      )} performance predictions`,
      tooltip: '',
    },
    {
      text: '5,000 performance data rows (connect your marketing channel and learn from past campaigns)',
      tooltip:
        'Connect your Hubspot, Google, Meta, website, and various other accounts to acquire copy insights and enable more personalized content creation. Each row (copy asset) represents a distinct copy asset that you employ and analyze. The number of rows depends on the number of integrations you have and the level of data granularity for each integration.',
    },
    {
      text: 'Unlimited copy generation',
      tooltip: '',
      hiddenOnPricingPage: true,
    },
    {
      text: '5 brand voices',
      tooltip:
        'Create up to 5 brand voices and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
      hiddenOnPricingPage: true,
    },
    {
      text: seatsTextFormatter,
      tooltip: createSeatsTooltipFormatter(3, 20),
      exclusiveForPricingPage: true,
    },
    {
      text: 'Custom-built AI models',
      tooltip:
        'Develop a custom AI model tailored to your brand with Anyword’s data science experts.',
    },
    {
      text: 'Real-time content intelligence across your marketing channels',
      tooltip: '',
    },
    {
      text: 'Benchmark new generated content against content you published in the past',
      tooltip: '',
    },
    {
      text: 'Identify high-performing talking points & incorporate them into new content',
      tooltip: '',
    },
    {
      text: 'Automated website messaging A/B testing',
      tooltip: '',
    },
    {
      text: 'Real-time performance predictions for manual edits',
      tooltip: '',
      hiddenOnPricingPage: true,
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
      hiddenOnPricingPage: true,
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip: '',
      hiddenOnPricingPage: true,
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog\nin minutes.',
      hiddenOnPricingPage: true,
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
      hiddenOnPricingPage: true,
    },
    {
      text: workspacesTextFormatter,
      tooltip: '',
      hiddenOnPricingPage: true,
    },
    {
      text: 'Onboarding & account setup',
      tooltip:
        'Work with an Anyword expert to set up your account, train Anyword on your brand & develop an AI action plan for success.',
    },
    {
      text: 'Roles & permissions management',
      tooltip: "Easily manage roles, permissions, and access across your company's workspaces.",
    },
    {
      text: 'Priority support',
      tooltip: '',
    },
  ],
});

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_ACTIVE.performance, {
  featuresTitle: 'Everything In Business, plus:',
  features: [
    {
      text: `${getPlanPerformancePredictionsLimit(
        SUBSCRIPTION_PLAN.performance
      )}+ performance predictions`,
      tooltip: '',
    },
    {
      text: '10,000+ performance data rows (connect your marketing channel and learn from past campaigns)',
      tooltip:
        'Connect your Hubspot, Google, Meta, website, and various other accounts to acquire copy insights and enable more personalized content creation. Each row (copy asset) represents a distinct copy asset that you employ and analyze. The number of rows depends on the number of integrations you have and the level of data granularity for each integration.',
    },
    {
      text: 'Enterprise-grade security & SSO',
      tooltip:
        'A comprehensive set of enterprise-grade security features, including SOC2 and ISO compliance and single sign-on to authenticate all users’ logins.',
    },
    {
      text: 'Private language model (LLM)',
      tooltip:
        'Build & fine-tune a secure, dedicated language model that serves only your organization to\nensure you maintain control over sensitive\ndata and information.',
    },
    {
      text: 'Custom integrations',
      tooltip: 'Connect any of your existing marketing tech\nstack to Anyword.',
    },
    {
      text: 'Custom-built AI models',
      tooltip:
        'Develop a custom AI model tailored to your brand with Anyword’s data science experts.',
    },
    {
      text: 'Full API access',
      tooltip: "Use Anyword's API to build performance-focused applications and processes.",
    },
    {
      text: 'Security & legal review',
      tooltip: '',
    },
    {
      text: 'Priority support',
      tooltip:
        'Get faster, prioritized technical and customer support from our dedicated Support specialists.',
    },
    {
      text: 'Dedicated customer success manager',
      tooltip:
        'Dedicated Anyword expert to help you set up your account, train Anyword on your brand, develop an AI action plan, and develop best practices for your team through dedicated consultation hours.',
    },
  ],
});

/**
 * Legacy plans features
 */

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_LEGACY.starterLegacy, {
  features: [
    {
      text: 'Unlimited performance predictions (Legacy plan)',
      tooltip: '',
    },
    {
      text: 'Unlimited copy generation',
      tooltip:
        'You have unlimited words to generate as much content as you need with Anyword’s Data-driven editor and Long form wizard.',
    },
    {
      text: '1 brand voice',
      tooltip:
        'Create 1 brand voice and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip:
        'Choose from our ready-made marketing prompts for emails, social media, ads, landing pages, etc.',
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog in minutes.',
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
    },
    {
      text: workspacesTextFormatter,
      tooltip: '',
    },
  ],
});

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_LEGACY.ddTeamsLegacy, {
  features: [
    {
      text: 'Unlimited performance predictions (Legacy plan)',
      tooltip: '',
    },
    {
      text: 'Real-time performance predictions for manual edits',
      tooltip: '',
    },
    {
      text: 'Unlimited copy generation',
      tooltip:
        'You have unlimited words to generate as much content as you need with Anyword’s Data-driven editor and Long form wizard.',
    },
    {
      text: '1 brand voice',
      tooltip:
        'Create 1 brand voice and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip:
        'Choose from our ready-made marketing prompts for emails, social media, ads, landing pages, etc.',
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog\nin minutes.',
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
    },
    {
      text: workspacesTextFormatter,
      tooltip: '',
    },
  ],
});

PLAN_FEATURES_MAP.add(SUBSCRIPTION_PLAN_LEGACY.businessSelfServeLegacy, {
  features: [
    {
      text: 'Unlimited performance predictions (Legacy plan)',
      tooltip: '',
    },
    {
      text: 'Unlimited copy generation',
      tooltip: '',
    },
    {
      text: '5 brand voices',
      tooltip:
        'Create up to 5 brand voices and add in your tone of voice, target audiences, brand terms, messaging bank and more.',
    },
    {
      text: 'Connect your marketing channel and learn from past campaigns (upload up to 5,000 performance data rows)',
      tooltip:
        'Connect your Hubspot, Google, Meta, website, and various other accounts to acquire copy insights and enable more personalized content creation. Each row (copy asset) represents a distinct copy asset that you employ and analyze. The number of rows depends on the number of integrations you have and the level of data granularity for each integration.',
    },
    {
      text: 'Custom-built AI models',
      tooltip:
        'Develop a custom AI model tailored to your brand with Anyword’s data science experts.',
    },
    {
      text: 'Real-time content intelligence across your marketing channels',
      tooltip: '',
    },
    {
      text: 'Benchmark new generated content against content you published in the past',
      tooltip: '',
    },
    {
      text: 'Identify high-performing talking points & incorporate them into new content',
      tooltip: '',
    },
    {
      text: 'Automated website messaging A/B testing',
      tooltip: '',
    },
    {
      text: 'Real-time performance predictions for manual edits',
      tooltip: '',
    },
    {
      text: 'One-click boost performance & content improver',
      tooltip: '',
    },
    {
      text: '100+ marketing templates & prompts',
      tooltip: '',
    },
    {
      text: 'Long form wizard with plagiarism checker',
      tooltip: 'Create an SEO-optimized, plagiarism-free blog\nin minutes.',
    },
    {
      text: 'Anyword’s Google Chrome Extension',
      tooltip:
        'Use Anyword’s marketing templates, predictive performance analytics and brand voice features directly in ChatGPT, Google Docs, Notion\nand more.',
    },
    {
      text: workspacesTextFormatter,
      tooltip: '',
    },
    {
      text: 'Onboarding & account setup',
      tooltip:
        'Work with an Anyword expert to set up your account, train Anyword on your brand & develop an AI action plan for success.',
    },
    {
      text: 'Roles & permissions management',
      tooltip: "Easily manage roles, permissions, and access across your company's workspaces.",
    },
    {
      text: 'Priority support',
      tooltip: '',
    },
  ],
});

const getProperty = (plan, property, defaultValue = undefined) => {
  const props = PLAN_FEATURES_MAP.get(plan);
  return (props && props[property]) || defaultValue;
};

export const getSubscriptionPlanFeatures = (plan) => getProperty(plan, 'features', []);
export const getSubscriptionPlanFeaturesTitle = (plan) => getProperty(plan, 'featuresTitle', []);

export const planHasFeatures = (plan) => getSubscriptionPlanFeatures(plan)?.length > 0;

export const planFeatureIsExclusivePricingPage = (feature) =>
  feature?.exclusiveForPricingPage || false;
export const planFeatureIsHiddenOnPricingPage = (feature) => feature?.hiddenOnPricingPage || false;
